/**
 * /crm/shrinkage — shrinkage incidents queue with KPIs.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { useCrmShrinkage, type CrmShrinkageStatus } from '@/api/bi/crm';
import { SHRINKAGE_STATUS_TONE } from '@/lib/crmStatus';
import { fmt } from '@/lib/format';

const FILTERS: Array<{ value: CrmShrinkageStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'reported', label: 'Reported' },
  { value: 'verified', label: 'Verified (waiting approval)' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export function CrmShrinkagePage() {
  const [status, setStatus] = useState<CrmShrinkageStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const incidents = useCrmShrinkage({
    status: status === 'all' ? null : status,
    page,
    pageSize: 25,
  });
  const rows = incidents.data?.results ?? [];
  const pagination = incidents.data?.pagination;

  const totals = useMemo(() => {
    let totalLoss = 0;
    let approvedLoss = 0;
    let pending = 0;
    for (const r of rows) {
      const loss = Number(r.total_estimated_loss) || 0;
      totalLoss += loss;
      if (r.verification_status === 'approved') approvedLoss += loss;
      if (r.verification_status === 'reported' || r.verification_status === 'verified') {
        pending += 1;
      }
    }
    return { totalLoss, approvedLoss, pending };
  }, [rows]);

  // Summing an empty array gives zero, and a confident "Rs 0 lost" is the one
  // answer these tiles must not give while the page is still loading or the
  // request has failed.
  const tileState: TotalState = incidents.isLoading
    ? 'loading'
    : incidents.isError
      ? 'error'
      : 'ready';

  return (
    <>
      <PageHeader
        eyebrow="CRM"
        title="Shrinkage"
        description="Stock loss reported by support / day-close / audit. Verify the incident, then approve to lock the financial loss for accounting."
      />

      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {/* These are page totals, not queue totals — the label says so, because
            an unqualified "total loss" that changes when you paginate is worse
            than no number at all. */}
        <PageTotal
          label="Total estimated loss (page)"
          value={fmt.npr(totals.totalLoss)}
          state={tileState}
        />
        <PageTotal
          label="Approved loss (page)"
          value={fmt.npr(totals.approvedLoss)}
          state={tileState}
        />
        <PageTotal label="Pending review" value={String(totals.pending)} state={tileState} />
      </div>

      <div className="mb-3 flex items-center gap-2">
        <Select value={status} onValueChange={(v) => { setStatus(v as never); setPage(1); }}>
          <SelectTrigger className="h-9 w-56 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground">
          {pagination
            ? `${pagination.total.toLocaleString()} incident${pagination.total === 1 ? '' : 's'}`
            : ''}
        </div>
      </div>

      <Card>
        <CardBody className="p-0">
          {incidents.isLoading ? (
            <div className="space-y-2 px-5 py-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : incidents.isError ? (
            <div className="px-5 py-4">
              <ErrorState
                description="Could not load shrinkage incidents."
                onRetry={() => void incidents.refetch()}
              />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ShieldAlert className="h-5 w-5" />}
              title="No shrinkage in view"
              description="Adjust the filter or open a ticket to record one."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Incident</th>
                    <th className="px-4 py-2 text-left">Store</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-right">Lines</th>
                    <th className="px-4 py-2 text-right">Loss</th>
                    <th className="px-4 py-2 text-right">Discovered</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-2">
                        <Link to={`/crm/tickets/${s.ticket_id}`} className="font-medium hover:underline">
                          Ticket #{s.ticket_id}
                        </Link>
                      </td>
                      <td className="px-4 py-2">{s.store_name ?? '—'}</td>
                      <td className="px-4 py-2">
                        <Badge tone={SHRINKAGE_STATUS_TONE[s.verification_status]}>
                          {s.verification_status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-right">{s.items.length}</td>
                      <td className="px-4 py-2 text-right">{fmt.npr(s.total_estimated_loss)}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">
                        {s.discovered_at ? fmt.ago(s.discovered_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {pagination && pagination.total_pages > 1 ? (
        <div className="mt-3 flex items-center justify-end gap-2 text-xs">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={pagination.page <= 1}
            className="press rounded-md border border-border px-2 py-1 disabled:opacity-50"
          >
            Prev
          </button>
          <span>
            {pagination.page} / {pagination.total_pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            disabled={pagination.page >= pagination.total_pages}
            className="press rounded-md border border-border px-2 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      ) : null}
    </>
  );
}

type TotalState = 'loading' | 'error' | 'ready';

function PageTotal({ label, value, state }: { label: string; value: string; state: TotalState }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{label}</CardTitle>
      </CardHeader>
      <CardBody>
        {state === 'loading' ? (
          <Skeleton className="h-8 w-28" />
        ) : state === 'error' ? (
          <div className="text-2xl text-muted-foreground" title="Could not load incidents">
            —
          </div>
        ) : (
          <div className="kpi-num text-2xl">{value}</div>
        )}
      </CardBody>
    </Card>
  );
}
